import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Check, X, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

function PricingCard({ tier }) {
  const { name, price, ideal, features, notIncluded, popular, color } = tier;
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/contact#contact-form", {
      state: { selectedPlan: { name, price, features } },
    });
  };

  return (
    <div
      data-testid={`pricing-card-${name.toLowerCase()}`}
      className={`pricing-card relative p-6 lg:p-8 rounded-2xl border h-full flex flex-col ${
        popular ? "bg-gray-900 border-cyan-500/50" : "bg-gray-900/50 border-gray-800"
      }`}
    >
      {popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 flex items-center gap-1">
          <span className="px-4 py-1 rounded-full bg-cyan-500 text-white text-xs font-medium flex items-center gap-1">
            <Star size={12} fill="white" /> MOST POPULAR
          </span>
        </div>
      )}

      <div className={`w-3 h-3 rounded-full mb-4 ${color}`} />

      <div className="mb-4">
        <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
        <p className="text-gray-400 text-sm">{ideal}</p>
      </div>

      <div className="mb-6">
        <span className="text-4xl font-bold text-white">£{price}</span>
        <span className="text-gray-400">/month</span>
      </div>

      <div className="flex-1">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Included</p>
        <ul className="space-y-2 mb-6">
          {features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <Check size={16} className="text-cyan-500 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300 text-sm">{feature}</span>
            </li>
          ))}
        </ul>

        {notIncluded && notIncluded.length > 0 && (
          <>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Not Included</p>
            <ul className="space-y-2 mb-6">
              {notIncluded.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <X size={16} className="text-gray-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-500 text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <Button
        onClick={handleGetStarted}
        data-testid={`pricing-cta-${name.toLowerCase()}`}
        className={`w-full font-medium mt-auto ${
          popular ? "btn-primary text-white" : "bg-gray-800 text-white hover:bg-gray-700"
        }`}
      >
        Get Started
      </Button>
    </div>
  );
}

export default function PricingGrid({ tiers }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-6">
      {tiers.map((tier, index) => (
        <motion.div
          key={tier.name}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
        >
          <PricingCard tier={tier} />
        </motion.div>
      ))}
    </div>
  );
}
